import { AgencyId, AgentUserRole, Dtos } from "@inrev/common";
import { useAdminInviteAgent } from "@portal/api/agency/admin";
import { useInviteAgentUser } from "@portal/api/user/agent";
import { FormField, FormFieldGroup, FormItemLabel } from "@portal/components/form";
import { FormDropdown, FormInput } from "@portal/components/form/fields";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Button } from "@portal/components/primitives/Button";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { stripEmptyResolver, typedZodResolver } from "../../../utils/form";

type AgentInviteFormModalProps = {
	isAdmin?: boolean;
	agencyId?: AgencyId;
	onClose: () => void;
};

type AgentInviteFormData = {
	email: string | "";
	role: AgentUserRole | "";
};

const getDefaultFormData = (): AgentInviteFormData => ({
	email: "",
	role: "",
});

export const AgentInviteFormModal = ({ agencyId, onClose }: AgentInviteFormModalProps) => {
	const {
		inviteAgentUser,
		inviteAgentUserIsLoading,
		inviteAgentUserError,
		inviteAgentUserIsSuccess,
	} = useInviteAgentUser();
	const {
		adminInviteAgent,
		adminInviteAgentIsLoading,
		adminInviteAgentError,
		adminInviteAgentIsSuccess,
	} = useAdminInviteAgent();

	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	const formMethods = useForm<AgentInviteFormData, any, Dtos.Agent.Invite.Request>({
		defaultValues: getDefaultFormData(),
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(
			// biome-ignore lint/suspicious/noExplicitAny: legacy any
			typedZodResolver<AgentInviteFormData, any, Dtos.Agent.Invite.Request>(
				Dtos.Agent.Invite.Request.schema,
			),
		),
	});

	const isAdmin = agencyId !== undefined;
	const fieldsDisabled =
		(isAdmin && adminInviteAgentIsLoading) || (!isAdmin && inviteAgentUserIsLoading);

	const handleSubmit = (data: Dtos.Agent.Invite.Request) => {
		if (agencyId) {
			adminInviteAgent({ agencyId, data });
		} else {
			inviteAgentUser(data);
		}
	};

	useEffect(() => {
		if (isAdmin) {
			if (
				!adminInviteAgentIsLoading &&
				(adminInviteAgentIsSuccess || adminInviteAgentError !== null)
			) {
				onClose();
			}
		} else {
			if (
				!inviteAgentUserIsLoading &&
				(inviteAgentUserIsSuccess || inviteAgentUserError !== null)
			) {
				onClose();
			}
		}
	}, [inviteAgentUserIsLoading, adminInviteAgentIsLoading, isAdmin]);

	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader
				header={isAdmin ? "Invite an Agent" : "Invite a Team Member"}
				className="w-[500px]"
				bodyClassName="py-[25px] px-[30px]"
				onClose={onClose}
			>
				<form onSubmit={formMethods.handleSubmit(handleSubmit)}>
					<FormFieldGroup>
						<FormField>
							<FormItemLabel required>Email</FormItemLabel>
							<FormInput control={formMethods.control} name="email" disabled={fieldsDisabled} />
						</FormField>
						<FormField>
							<FormItemLabel required>Role</FormItemLabel>
							<FormDropdown
								control={formMethods.control}
								name="role"
								options={[
									{ label: "Agent Admin", value: "agent-admin" },
									{ label: "Agent", value: "agent" },
								]}
								placeholder="Select one"
								readonly={fieldsDisabled}
							/>
						</FormField>
					</FormFieldGroup>
					<div className="flex justify-center gap-[10px] mt-[35px]">
						<Button
							type="submit"
							color="primary"
							variant="solid"
							loading={fieldsDisabled}
							className="order-2"
						>
							Invite
						</Button>
						<Button
							type="button"
							color="secondary"
							variant="outline"
							onClick={onClose}
							className="order-1"
						>
							Cancel
						</Button>
					</div>
				</form>
			</ModalItemWithHeader>
		</Modal>
	);
};
